import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PrintHelper from 'print-helper-lib';
import { isRepeatingPaymentToView, getToView, getActiveAwaitingAuthTab } from '../../states/payments/builders';
import { verificationPropTypes } from '../../states/payments/propTypes';
import { goBackPage } from '../../states/page/actions';
import { isUserCA } from '../../states/user/selectors';
import { getBankOrBrandId } from '../../states/envConfig/selectors';

import SinglePaymentDetails from '../PaymentDetails/SinglePaymentDetails/SinglePaymentDetails';
import RepeatingPaymentDetails from '../PaymentDetails/RepeatingPaymentDetails/RepeatingPaymentDetails';
import BacsCreditPaymentDetails from '../PaymentDetails/bacs/BacsCreditPaymentDetails';
import ChapsPaymentDetails from '../PaymentDetails/chaps/ChapsPaymentDetails';
import BacsDebitPaymentDetails from '../PaymentDetails/bacs/BacsDebitPaymentDetails';

import withContent from '../../containers/withContent';
import withScrollTop from '../../containers/withScrollTop';
import viewPaymentContent, { contentPropTypes } from './ViewPayment.content';
import { BACS_CREDIT_CODE, BACS_DEBIT_CODE, TYPE_IFT, TYPE_CHAPS } from '../../config';
import ViewIFTPayment from './ViewIFTPayment';
import { HARD, VERIFIED } from '../../constants/Constants';
import { getHasGroupApprovalsEnabled } from '../../states/session/selectors';

import { Grid } from 'web-ui-components/lib/layout/grids';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import { SubHeading, SectionHeading } from 'web-ui-components/lib/typography/headings';
import { VerticalReviewTable } from 'web-ui-components/lib/layout/tables';
import { StatusTag } from 'web-ui-components/lib/typography/tags';
import { StandardBody } from 'web-ui-components/lib/typography/body';

const mapState = (state) => ({
	isRepeatingPayment: isRepeatingPaymentToView(state),
	toView: getToView(state),
	isCA: isUserCA(state),
	brandId: getBankOrBrandId(state),
	hasGroupApprovalsEnabled: getHasGroupApprovalsEnabled(state),
	getActiveAwaitingAuthTab: getActiveAwaitingAuthTab(state),
});

const mapDispatch = (dispatch) => ({
	goBack: () => dispatch(goBackPage()),
});

const ViewPayment = ({
	content,
	isRepeatingPayment,
	toView,
	goBack,
	isCA,
	brandId,
	hasGroupApprovalsEnabled,
	getActiveAwaitingAuthTab,
}) => {
	const {
		title,
		paymentStatusTitle,
		statusLabel,
		authRequiredLabel,
		howToSelfAuthoriseTitle,
		howToSelfAuthoriseBody,
		howToThirdPartyAuthorise,
		howToSelfAuthoriseBodyHardToken,
		howToThirdPartyAuthorisePlural,
		howtoAuthoriseGroupApprovals,
		back,
		howToCAAuthorise,
	} = content;

	const getHowToAuthoriseContent = () => {
		if (hasGroupApprovalsEnabled) {
			if (getActiveAwaitingAuthTab == 'others') {
				return (
					<StandardBody>
						<p>{howtoAuthoriseGroupApprovals}</p>
					</StandardBody>
				);
			}
			return (
				<StandardBody>
					<p>{howToSelfAuthoriseTitle}</p>
					<p>{howToSelfAuthoriseBody}</p>
				</StandardBody>
			);
		}
		if (toView.isThirdParty) {
			const getApprovers = (approvers = []) =>
				approvers.filter((approver) => approver.status !== VERIFIED).map(({ approver }) => approver);
			const approvers = getApprovers(toView.approvers);
			return (
				<StandardBody>
					<p>By {approvers.join(' and ')}</p>
					<p>{approvers.length > 1 ? howToThirdPartyAuthorisePlural : howToThirdPartyAuthorise}</p>
				</StandardBody>
			);
		}
		if (isCA) {
			return (
				<StandardBody>
					<p>{howToCAAuthorise}</p>
				</StandardBody>
			);
		}
		if (toView.verification_type === HARD) {
			return (
				<StandardBody>
					<p>{howToSelfAuthoriseBodyHardToken}</p>
				</StandardBody>
			);
		}
		return (
			<StandardBody>
				<p>{howToSelfAuthoriseTitle}</p>
				<p>{howToSelfAuthoriseBody}</p>
			</StandardBody>
		);
	};

	const renderPaymentDetails = () => {
		if (isRepeatingPayment) return <RepeatingPaymentDetails payment={toView} />;
		if (toView.bacsType === BACS_CREDIT_CODE) return <BacsCreditPaymentDetails payment={toView} />;
		if (toView.bacsType === BACS_DEBIT_CODE) return <BacsDebitPaymentDetails payment={toView} />;
		if (toView.type === TYPE_CHAPS) return <ChapsPaymentDetails payment={toView} />;
		if (toView.type === TYPE_IFT) return <ViewIFTPayment paymentToView={toView} />;
		return <SinglePaymentDetails payment={toView} />;
	};

	const statusData = [
		{
			headerText: statusLabel,
			cellText: <StatusTag.WarningMinor isFlush>Awaiting Authorisation</StatusTag.WarningMinor>,
		},
		{ headerText: authRequiredLabel, cellText: getHowToAuthoriseContent() },
	];

	return (
		<div>
			<BackButton onClick={goBack} />
			<SubHeading>{title}</SubHeading>

			<Grid.TwoThirds>
				<div className="u-margin-bottom-4">
					<SectionHeading>{paymentStatusTitle}</SectionHeading>
					<PrintHelper className="web-ui-components" brandId={brandId} showPrintTopIcon showPrintBottomIcon={false} />
					<VerticalReviewTable data={statusData} data-test-id="view-payment-table" />
				</div>
				{renderPaymentDetails()}
			</Grid.TwoThirds>
			<Button id="button-back" display="secondary" width="flush" onClick={goBack}>
				{back}
			</Button>
		</div>
	);
};

ViewPayment.propTypes = {
	content: PropTypes.shape(contentPropTypes),
	isRepeatingPayment: PropTypes.bool,
	toView: PropTypes.shape(verificationPropTypes).isRequired,
	goBack: PropTypes.func.isRequired,
	isCA: PropTypes.bool.isRequired,
	brandId: PropTypes.string.isRequired,
};

export default compose(
	connect(mapState, mapDispatch),
	withContent({ content: viewPaymentContent }),
	withScrollTop()
)(ViewPayment);
export const ConnectWrapper = connect(mapState)(ViewPayment);
